"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BarChartComponent = void 0;
var d3 = require("d3");
var React = require("react");
var react_1 = require("react");
var styles = require("../styles/ChartStyles");
var colors = require("../util/colors");
var sizes = require("../util/sizes");
var utils_1 = require("../util/utils");
var CHART_CONFIG = {
    fontSize: '12px',
    legendHeight: 16,
    legendCircleRadius: 6,
};
var BarChartComponent = function (props) {
    var data = props.data, title = props.title, xAxisLabel = props.xAxisLabel, yAxisLabel = props.yAxisLabel, referenceLine = props.referenceLine, legend = props.legend;
    var chartRef = react_1.useRef(null);
    var xDomain = data.map(function (d) { return d.name; });
    var yDomain = [0, d3.max(data.map(function (d) { return d.count; })) || 0];
    var height = props.height || sizes.heightRegular;
    var width = props.width || sizes.widthWide;
    var margin = sizes.chartMargins;
    var chartHeight = height - margin.top - margin.bottom;
    var chartWidth = width - margin.left - margin.right;
    var xScale = d3
        .scaleBand()
        .domain(xDomain)
        .rangeRound([margin.left, width - margin.right])
        .padding(0.1);
    var yScale = d3
        .scaleLinear()
        .domain(yDomain)
        .range([height - margin.bottom, margin.top]);
    var drawXAxisBarLabels = react_1.useCallback(function (xAxisTranslate, yAxisTranslate) {
        var svg = d3.select(chartRef.current);
        var labelWidth = (xScale.range()[1] - xScale.range()[0]) / xDomain.length;
        var labelHtml = xDomain
            .map(function (v) { return "<span class=\"" + styles.barAxisLabel + "\" title=\"" + v + "\" style=\"width: " + labelWidth + "px\">" + v + "</span>"; })
            .join('');
        svg
            .append('foreignObject')
            .attr('width', xScale.range()[1] - xScale.range()[0])
            .attr('height', 20)
            .attr('x', yAxisTranslate)
            .attr('y', xAxisTranslate)
            .append('xhtml:div')
            .html(labelHtml);
    }, [xDomain, xScale]);
    var drawAxes = react_1.useCallback(function () {
        var svg = d3.select(chartRef.current);
        var xAxisTranslate = height - margin.bottom;
        var yAxisTranslate = margin.left;
        // Draw X axis bar text
        drawXAxisBarLabels(xAxisTranslate, yAxisTranslate);
        // Draw Y axis bar text
        var yAxis = utils_1.getLinearAxis(yScale, utils_1.AxisPosition.Left);
        utils_1.formatAxis(yAxis, 5, sizes.defaultTickSize, sizes.defaultTickPadding, utils_1.TickFormat.formatK);
        utils_1.drawAxis(svg, yAxis, yAxisTranslate, 0, CHART_CONFIG.fontSize, styles.barAxisText);
        var backgroundAxis = utils_1.getLinearAxis(yScale, utils_1.AxisPosition.Left);
        utils_1.formatAxis(backgroundAxis, 5, -chartWidth, sizes.defaultTickPadding, utils_1.TickFormat.hideNumbers);
        utils_1.drawAxisGridLines(svg, backgroundAxis, yAxisTranslate, 0);
    }, [chartWidth, height, margin, yScale, drawXAxisBarLabels]);
    var drawBars = react_1.useCallback(function () {
        var bars = d3.select(chartRef.current).selectAll('rect').data(data).enter();
        var getYAxisScale = function (count) {
            var scale = yScale(count);
            return scale ? scale - margin.top : 0;
        };
        var getBarHeight = function (count) {
            var scale = yScale(count);
            if (!scale)
                return 0;
            return chartHeight - scale + margin.top;
        };
        bars
            .append('rect')
            .attr('x', function (d) { return xScale(d.name) || 0; })
            .attr('y', function (d) {
            return getYAxisScale(d.count);
        })
            .attr('transform', 'translate(0,' + margin.top.toString() + ')')
            .attr('width', xScale.bandwidth())
            .attr('height', function (d) {
            return getBarHeight(d.count);
        })
            .attr('fill', function (d) { return d.color || colors.background01; });
        bars
            .append('text')
            .attr('font-size', '12')
            .attr('text-anchor', 'middle')
            .attr('x', function (d) {
            var startReposition = xScale(d.name) || 0;
            return startReposition + xScale.bandwidth() / 2;
        })
            .attr('y', function (d) {
            var endReposition = yScale(d.count) || 0;
            return endReposition - 1;
        })
            .text(function (d) {
            return d.count;
        });
        if (referenceLine) {
            // Calculation is bit tricky here, because we start from up to bottom to calculate the reference line height
            var referenceScale = 100 - 100 * referenceLine.value;
            var referenceLineHeight = chartHeight - getYAxisScale(referenceScale) + margin.top;
            bars
                .append('line')
                .style('stroke', referenceLine.color)
                .attr('x1', (xScale(data[0].name) || 0) - 20)
                .attr('y1', referenceLineHeight)
                .attr('x2', (xScale(data[data.length - 1].name) || 0) + xScale.bandwidth() + 20)
                .attr('y2', referenceLineHeight);
        }
    }, [chartHeight, data, margin, xScale, yScale, referenceLine]);
    var drawLegend = react_1.useCallback(function () {
        if (!legend) {
            return;
        }
        var svg = d3.select(chartRef.current);
        var baseStartY = sizes.heightRegular;
        var baseCircleStartX = width / 3;
        var baseTextStartX = baseCircleStartX + 2 * CHART_CONFIG.legendCircleRadius;
        for (var i = 0; i < legend.length; i++) {
            var legendItem = legend[i];
            var delta = i * CHART_CONFIG.legendHeight;
            svg
                .append('circle')
                .attr('cx', baseCircleStartX)
                .attr('cy', baseStartY + delta)
                .attr('r', CHART_CONFIG.legendCircleRadius)
                .style('fill', legendItem.color);
            svg
                .append('text')
                .attr('x', baseTextStartX)
                .attr('y', baseStartY + delta)
                .attr('class', styles.barAxisText)
                .text(legendItem.text)
                .style('font-size', CHART_CONFIG.fontSize)
                .attr('alignment-baseline', 'middle');
        }
    }, [legend, width]);
    react_1.useEffect(function () {
        d3.select(chartRef.current).selectAll('*').remove();
        var legendHeight = CHART_CONFIG.legendHeight * (legend || []).length;
        var svg = d3
            .select(chartRef.current)
            .attr('height', height + legendHeight)
            .attr('width', width);
        drawAxes();
        drawBars();
        var xAxisLabelStartX = (chartWidth + margin.left + margin.right) / 2;
        var xAxisLabelStartY = chartHeight + margin.top + margin.bottom;
        utils_1.drawAxisLabel(svg, xAxisLabel || '', xAxisLabelStartX, xAxisLabelStartY, false, CHART_CONFIG.fontSize, styles.barAxisText);
        var yAxisLabelStartX = 10;
        var yAxisLabelStartY = chartHeight / 2 + margin.top;
        utils_1.drawAxisLabel(svg, yAxisLabel || '', yAxisLabelStartX, yAxisLabelStartY, true, CHART_CONFIG.fontSize, styles.barAxisText);
        drawLegend();
    }, [chartHeight, chartWidth, drawAxes, drawBars, drawLegend, height, margin, width, xAxisLabel, yAxisLabel, legend]);
    return (React.createElement("div", null,
        title,
        React.createElement("svg", { ref: chartRef })));
};
exports.BarChartComponent = BarChartComponent;
//# sourceMappingURL=BarChartComponent.js.map